import { useState, useEffect, useRef } from "react";
import {
  retrieveSakPhotoData,
  retrieveSakQuestionnaireData,
  storeSakPhotoData,
  storeSakQuestionnaireData,
  triggerEvent
} from "../lib";
import { setPostPurchaseStartTimestamp } from "../lib/observability/observers";
import { ChildData } from "../types";
import ErrorState from "./ErrorState";
import StartPage from "./StartPage";
import LoadingOverlay from "./LoadingOverlay";
import PhotoUpload from "./PhotoUpload/PhotoUpload";
import appSettings, { updateSponsoredChildData } from "../settings/appSettings";
import Questionnaire from "./Questionnaire/Questionnaire";
import CardPreviewContainer from "./CardPreview/CardPreviewContainer";
import SentPage from "./SentPage";
import Header from "./Header";
import { useChildData } from "../lib/hooks/useChildData";
import { useFormFlow, StepStatus } from "../lib/hooks/useFormFlow";
import { useEletterSubmission } from "../lib/hooks/useEletterSubmission";

interface AppProps {
  campaignSourceCode: string;
  childId: string;
  limitedTrustHandshake: string;
  limitedTrustMarker: string;
  supporterName: string;
  supporterNumber: string;
  supporterState: string;
}

interface PhotoData {
  mediaUrl: string;
  mediaHandshake: string;
}

interface QuestionnaireData {
  topic: string;
  response: string;
}

/**
 * Main application component that orchestrates the entire engagement flow
 *
 * This component is responsible for:
 * 1. Managing overall application state and form progression
 * 2. Tracking photo uploads and questionnaire responses
 * 3. Submitting final data to SAK/eletter services
 * 4. Handling responsive layout between mobile and desktop views
 * 5. Persisting user activity across sessions via local storage
 */
function App({
  campaignSourceCode = "",
  childId = "",
  limitedTrustHandshake = "",
  limitedTrustMarker = "",
  supporterName = "",
  supporterNumber = "",
  supporterState = ""
}: AppProps) {
  const { eletterConfig, rcdsConfig, photoModConfig } = appSettings;
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMissingParams, setHasMissingParams] = useState<boolean>(false);

  // Custom hooks for state management
  const { childData, error, isCompletedSubmission } = useChildData(childId);
  const { flowState, setStatus, startSending, sendSuccess, sendError, clearError } = useFormFlow(
    isCompletedSubmission ? "sent" : "start"
  );

  // Update the sponsored child data in appSettings when it changes
  useEffect(() => {
    updateSponsoredChildData(childData);
  }, [childData]);

  // Set post-purchase start timestamp once when the app loads
  useEffect(() => {
    setPostPurchaseStartTimestamp();
  }, []);

  // Reference to the CardPreviewContainer component for animations
  const cardPreviewContainerRef = useRef<{
    animateCardPreview: () => void;
  } | null>(null);

  // Photo and questionnaire data state
  const [uploadedPhotoData, setUploadedPhotoData] = useState<PhotoData | null>(() =>
    retrieveSakPhotoData()
  );
  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireData | null>(() =>
    retrieveSakQuestionnaireData()
  );
  // Keep track of the selected topic to persist between status changes
  const [selectedTopic, setSelectedTopic] = useState<string>("");

  // Determine if we're in desktop view (shared across components)
  const DESKTOP_WIDTH = 1024;
  const WIDE_DESKTOP_WIDTH = 1170;
  const [isDesktop, setIsDesktop] = useState<boolean>(window.innerWidth >= DESKTOP_WIDTH);
  const [isWideDesktop, setIsWideDesktop] = useState<boolean>(
    window.innerWidth >= WIDE_DESKTOP_WIDTH
  );

  // Set up resize listener to detect desktop/mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= DESKTOP_WIDTH);
      setIsWideDesktop(window.innerWidth >= WIDE_DESKTOP_WIDTH);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Eletter submission hook
  const { sendEletter } = useEletterSubmission({
    childData,
    childId,
    campaignSourceCode,
    limitedTrustHandshake,
    limitedTrustMarker,
    supporterName,
    supporterNumber,
    supporterState,
    uploadedPhotoData,
    eletterConfig,
    setLoading: (isLoading) => {
      setLoading(isLoading);
      if (isLoading) startSending();
    },
    onSuccess: () => {
      sendSuccess();
    },
    onError: (message) => {
      sendError(message);
    }
  });

  // Redirect when required props are missing
  useEffect(() => {
    const requiredParamsMissing =
      !childId ||
      !limitedTrustHandshake ||
      !limitedTrustMarker ||
      !supporterName ||
      !supporterNumber ||
      !supporterState ||
      !eletterConfig ||
      !rcdsConfig ||
      !photoModConfig;

    // Log which params are missing.
    if (requiredParamsMissing) {
      console.error("Missing required parameters:", {
        childId,
        limitedTrustHandshake,
        limitedTrustMarker,
        supporterName,
        supporterNumber,
        supporterState,
        eletterConfig,
        rcdsConfig,
        photoModConfig
      });
    }

    setHasMissingParams(requiredParamsMissing);
  }, [
    childId,
    eletterConfig,
    limitedTrustHandshake,
    limitedTrustMarker,
    photoModConfig,
    rcdsConfig,
    supporterName,
    supporterNumber,
    supporterState
  ]);

  /**
   * Navigate between application statuses based on header indicator clicks
   * Follows logical flow for different click scenarios based on current status and furthest achieved status
   */
  const handleHeaderStatusClick = (clickedStatus: "start" | "photo" | "message" | "sent") => {
    const currentStatus = flowState.status;
    const furthestStatus = flowState.furthestStatus;

    // If clicking on a section when already on that section, do nothing (except for 'sent' special case)
    if (clickedStatus === "start" && currentStatus === "start") {
      return;
    }

    // For sent status, we handle the special re-animation case
    if (clickedStatus === "sent" && currentStatus === "sent") {
      // Briefly reset to questionnaire-input and back to sent to trigger animation
      if (cardPreviewContainerRef.current) {
        // Skip status change and directly re-animate the card preview
        cardPreviewContainerRef.current.animateCardPreview();
      }
      return;
    }

    // Standard navigation based on furthest progress
    switch (clickedStatus) {
      case "start":
        setStatus("start");
        break;

      case "photo": {
        // If the user has gotten to questionnaire or sent, always use photo-review since
        // they must have successfully uploaded a photo
        if (furthestStatus.startsWith("questionnaire-") || furthestStatus === "sent") {
          setStatus("photo-review");
        }
        // Otherwise navigate to the furthest photo status they've actually reached
        else {
          const photoStatuses = ["photo-review", "photo-pending"]; // exclude photo-rejected
          const furthestPhotoStatus = photoStatuses.find((status) => status === furthestStatus);
          setStatus((furthestPhotoStatus || "photo-pending") as StepStatus);
        }
        break;
      }

      case "message": {
        // Navigate to furthest questionnaire status the user has achieved
        if (furthestStatus === "questionnaire-input" || furthestStatus === "sent") {
          setStatus("questionnaire-input");
        } else {
          setStatus("questionnaire-topic");
        }
        break;
      }

      case "sent":
        // Only navigate to sent if user has reached it
        if (furthestStatus === "sent") {
          setStatus("sent");
        }
        break;
    }
  };

  // Handler functions for managing the progression through the application steps
  const handleGetStarted = () => setStatus("photo-pending");

  const handlePhotoConfirmed = (photoData: PhotoData) => {
    storeSakPhotoData(photoData);
    setUploadedPhotoData(photoData);
    window.scrollTo(0, 0);
    setStatus("questionnaire-topic");
  };

  const handlePhotoReview = (data: { imageUrl: string; imageHandshake: string }) => {
    setUploadedPhotoData({
      mediaUrl: data.imageUrl,
      mediaHandshake: data.imageHandshake
    });
  };

  const handleQuestionnaireSubmit = async (topic: string, response: string) => {
    // Store the topic in our app state as well for persistence
    setSelectedTopic(topic);
    const newQuestionnaireData = { topic, response };
    storeSakQuestionnaireData(newQuestionnaireData);
    setQuestionnaireData(newQuestionnaireData);
    await sendEletter();
  };

  const handleQuestionnaireView = (topic: string) => {
    triggerEvent("sak_message_compose_view", { topic });
  };

  const handleMessageInteractionStart = (topic: string) => {
    triggerEvent("sak_message_compose_start", { topic });
  };

  // Show error state if child data loading failed or required params are missing
  if (error || hasMissingParams) {
    return <ErrorState />;
  }

  return (
    <div className="app-wrapper">
      {/* Common header shown across all states */}
      <Header
        childData={childData}
        status={flowState.status}
        furthestStatus={flowState.furthestStatus}
        isDesktop={isDesktop}
        onStatusClick={handleHeaderStatusClick}
      />
      <div className="app-container">
        {/* Global loading overlay - appears during async operations */}
        {loading && <LoadingOverlay />}

        {flowState.status === "start" && childData && (
          <StartPage
            childName={childData.givenName}
            gender={childData.gender}
            photoUrl={childData.cgpUrl || ""}
            onGetStarted={handleGetStarted}
          />
        )}

        {/* Main application flow rendering - two panels for preview and interactive content */}
        {flowState.status !== "start" && (
          <div className="app-content-wrapper">
            {/* Preview is always shown in both mobile and desktop layouts */}
            <div className="preview-container-wrapper">
              <CardPreviewContainer
                ref={cardPreviewContainerRef}
                status={flowState.status}
                uploadedPhotoUrl={uploadedPhotoData?.mediaUrl || ""}
                surveyCompleted={!!questionnaireData}
              />
            </div>

            {/* Main content area */}
            <div className="content-container-wrapper">
              <div className="content-container">
                {/* Photo Upload component */}
                {["photo-pending", "photo-review", "photo-rejected"].includes(flowState.status) && (
                  <PhotoUpload
                    photoModerationStep={flowState.status}
                    setLoading={setLoading}
                    setPhotoModerationStep={(step: string) => setStatus(step as StepStatus)}
                    onPhotoConfirmed={handlePhotoConfirmed}
                    onPhotoReview={handlePhotoReview}
                    headingText="Add a photo"
                    paragraphText="Children love to see their sponsors!"
                    reviewParagraphText={`${childData?.givenName || ""} will love seeing you! Should we use this photo?`}
                    submitError={flowState.eletterError}
                    uploadedPhotoData={uploadedPhotoData || undefined}
                  />
                )}

                {/* Questionnaire component */}
                {(flowState.status === "questionnaire-topic" ||
                  flowState.status === "questionnaire-input") && (
                  <Questionnaire
                    onSubmit={handleQuestionnaireSubmit}
                    uploadedPhotoUrl={uploadedPhotoData?.mediaUrl || ""}
                    onBackToPhoto={() => {
                      clearError();
                      setStatus("photo-review");
                    }}
                    onSkip={sendEletter}
                    childName={childData?.givenName || ""}
                    onQuestionnaireView={handleQuestionnaireView}
                    onMessageInteractionStart={handleMessageInteractionStart}
                    isSubmitting={flowState.eletterSending}
                    errorMessage={flowState.eletterError}
                    onClearError={clearError}
                    status={flowState.status}
                    setStatus={setStatus}
                    selectedTopic={selectedTopic}
                    setSelectedTopic={setSelectedTopic}
                    isDesktop={isDesktop}
                    isWideDesktop={isWideDesktop}
                  />
                )}

                {/* Sent page component */}
                {flowState.status === "sent" && childData && (
                  <SentPage childName={childData.givenName} gender={childData.gender} />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Development debugging panel */}
        {process.env.NODE_ENV === "development" && (
          <DebugPanel
            childData={childData}
            status={flowState.status}
            furthestStatus={flowState.furthestStatus}
            setStatus={setStatus}
            uploadedPhotoData={uploadedPhotoData}
            appProps={{
              campaignSourceCode,
              childId,
              limitedTrustHandshake,
              limitedTrustMarker,
              supporterName,
              supporterNumber,
              supporterState
            }}
          />
        )}
      </div>
    </div>
  );
}

// Extracted Debug Panel component
interface DebugPanelProps {
  childData: ChildData | null;
  status: StepStatus;
  furthestStatus: StepStatus;
  setStatus: (status: StepStatus) => void;
  uploadedPhotoData: PhotoData | null;
  appProps: AppProps;
}

function DebugPanel({
  childData,
  status,
  furthestStatus,
  setStatus,
  uploadedPhotoData,
  appProps
}: DebugPanelProps) {
  const {
    campaignSourceCode,
    childId,
    limitedTrustHandshake,
    limitedTrustMarker,
    supporterName,
    supporterNumber,
    supporterState
  } = appProps;

  return (
    <div className="debug-info">
      <ul>
        <li>
          App (step) Status: {status}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as StepStatus)}
            style={{ marginLeft: "10px" }}
          >
            <option value="start">start</option>
            <option value="photo-pending">photo-pending</option>
            <option value="photo-review">photo-review</option>
            <option value="photo-rejected">photo-rejected</option>
            <option value="questionnaire-topic">questionnaire-topic</option>
            <option value="questionnaire-input">questionnaire-input</option>
            <option value="sent">sent</option>
          </select>
        </li>
        <li>Furthest Status: {furthestStatus}</li>
        <li>App Version: {import.meta.env.APP_VERSION}</li>
        <li>Campaign Source Code: {campaignSourceCode}</li>
        <li>Child ID: {childId}</li>
        <li>Child First Name: {childData?.givenName}</li>
        <li>Child Last Name: {childData?.lastName}</li>
        <li>Gender: {childData?.gender}</li>
        <li>Child Image: {childData?.cgpUrl}</li>
        <li>Limited Trust Handshake: {limitedTrustHandshake}</li>
        <li>Limited Trust Marker: {limitedTrustMarker}</li>
        <li>Supporter Name: {supporterName}</li>
        <li>Supporter Number: {supporterNumber}</li>
        <li>Supporter State: {supporterState}</li>
        {uploadedPhotoData && <li>Uploaded Photo: {uploadedPhotoData.mediaUrl}</li>}
      </ul>
    </div>
  );
}

export default App;
